/**
 * EditableCell dispatches each error-table cell to the right typed cell
 * component. Each typed cell owns its own input state, validation, and
 * commit-on-blur logic; the shared CellShell wraps every variant with the
 * error background + tooltip.
 */

import { TableCellType } from '@/types/property.types';

import DateCell from './cells/DateCell';
import NumberCell from './cells/NumberCell';
import SelectCell from './cells/SelectCell';
import TextCell from './cells/TextCell';
import ZipCell from './cells/ZipCell';
import type { CellChangeData } from './cells/types';

export type { CellChangeData };

interface EditableCellProps {
  value: string | null;
  id: number;
  isActive: boolean;
  options?: Array<{ value: string; label: string }>;
  type: TableCellType;
  isDisabled?: boolean;
  errorMessage?: string;
  setData: (data: CellChangeData) => void;
  name: string;
  isOptionalField?: boolean;
  isDecimal?: boolean;
  maxCount?: number;
  setMaxMin?: boolean;
  isOnlyText?: boolean;
}

const EditableCell = ({
  value,
  id,
  isActive,
  options,
  type,
  isDisabled,
  errorMessage,
  setData,
  name,
  isOptionalField,
  isDecimal,
  maxCount,
  setMaxMin,
  isOnlyText,
}: EditableCellProps) => {
  const base = {
    value,
    id,
    isActive,
    isDisabled,
    isOptionalField,
    errorMessage,
    setData,
    name,
  };

  switch (type) {
    case TableCellType.TEXT:
      return <TextCell {...base} isOnlyText={isOnlyText} maxCount={maxCount} />;
    case TableCellType.NUMBER:
      return <NumberCell {...base} isDecimal={isDecimal} maxCount={maxCount} />;
    case TableCellType.ZIP:
      return <ZipCell {...base} maxCount={maxCount} />;
    case TableCellType.DATE:
      return <DateCell {...base} setMaxMin={setMaxMin} />;
    case TableCellType.SELECT:
      return <SelectCell {...base} options={options} />;
    default:
      return null;
  }
};

export default EditableCell;
